import React, { useEffect, useState } from 'react';

import { Text } from '@/components/shared/element/text';
import { Box } from '@/components/shared/layout/box';
import { Container } from '@/components/shared/layout/container';
import { Section } from '@/components/shared/layout/section';
import { Image } from '@/components/shared/media/image';
import { generateBannerImageSources } from '@/components/shared/media/image/generate-sources';
import { BadgesWithButton } from '@/components/shared/my24/badges-with-button';
import { MiuraLogo } from '@/components/shared/my24/miura-logo';
import { styled } from '@/stitches.config';
import {
  IHeadline,
  IItem,
  IMy24ProductMenu,
  IMy24Tracking,
} from '@/types/shared';
import { PickupModelType } from '@/types/shared/my24/pickup-model';

const HeroSection = styled(Section, {
  '--splash-mobile-width': 360,
  '--splash-mobile-height': 640,
  '--splash-mobile-gap-x': 24,
  '--splash-mobile-gap-y': 40,

  '--splash-tablet-width': 768,
  '--splash-tablet-height': 1024,
  '--splash-tablet-gap-x': 48,
  '--splash-tablet-gap-y': 64,

  '--splash-desktop-width': 1440,
  '--splash-desktop-height': 800,
  '--splash-desktop-gap-x': 104,
  '--splash-desktop-gap-y': 48,
  '@maxlg': {
    '@media (orientation: portrait)': {
      aspectRatio: '2 / 3',
      '@supports (height: 100vh)': {
        height: 'calc(100vh - var(--header-height))',
      },
      '@supports (height: 100svh)': {
        height: 'calc(100svh - var(--header-height))',
      },
    },
    '@media (orientation: landscape)': {
      aspectRatio: '3 / 4',
    },
  },
  '@lg': {
    '@supports (height: 100vh)': {
      height: 'calc(100vh - var(--header-height))',
    },
  },
});

const HeroContent = styled(Box, {
  linearGradient: 'to bottom, rgba(0, 0, 0, 0) 60%, rgba(0, 0, 0, 1) 100%',
});

const HeroContentBottom = styled(Box, {
  '@media (orientation: portrait)': {
    bottom: `${(40 / 640) * 100 + 'vh'}`,
  },
  '@media (orientation: landscape)': {
    bottom: `${(40 / 360) * 100 + 'vw'}`,
  },
  '@lg': {
    bottom: `${(64 / 800) * 100 + 'vh'}`,
  },
});

export interface IMY24ProductHeroBanner {
  active?: boolean;
  isMiura?: boolean;
  model?: PickupModelType;
  menu?: IMy24ProductMenu;
  headline: IHeadline;
  items?: IItem[];
  dataTrack?: IMy24Tracking;
  isPreloadBanner?: boolean;
}

const LCVProductBanner: React.FC<IMY24ProductHeroBanner> = ({
  active,
  model,
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  menu: { hash },
  headline: { title },
  items,
  dataTrack = 'lcv-product',
  isMiura = true,
  isPreloadBanner = true,
}) => {
  const [mount, setMount] = useState<boolean>(false);
  useEffect(() => {
    const id = setTimeout(() => {
      requestAnimationFrame(() => {
        setMount(true);
      });
    }, 400);
    return () => {
      clearTimeout(id);
    };
  }, []);

  return (
    <>
      {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
      {/* @ts-ignore */}
      {!!active && items?.length > 0 && (
        <HeroSection
          backgroundColor="black"
          data-hash={hash}
          test="section_banner"
          className="h-screen-w-header"
        >
          {!!title && (
            <Text as="h1" size="h1" sr-only>
              {title}
            </Text>
          )}
          {items?.map((item, i) => (
            <React.Fragment key={i}>
              {!!item?.image?.src && (
                <Image
                  priority={isPreloadBanner}
                  src={item?.image?.src}
                  alt={item?.image?.alt}
                  width={600}
                  height={800}
                  layout="fill"
                  sources={generateBannerImageSources({
                    mobileSrc: item?.image?.src,
                    mobileOptions: {
                      crop: '3:4',
                    },
                    desktopSrc: item?.imageDesktop?.src,
                    desktopOptions: {
                      crop: '16:9',
                    },
                  })}
                />
              )}
              {!!isMiura && (
                <MiuraLogo
                  model={model}
                  logo={item?.logo}
                  logoDesktop={item?.logoDesktop}
                  show={mount}
                />
              )}
              <HeroContent
                position="absolute"
                top="0"
                left="0"
                width="full"
                height="full"
                zIndex="1"
              >
                <HeroContentBottom position="absolute" left="0" width="full">
                  <Container>
                    <BadgesWithButton
                      item={item}
                      dataTest="banner"
                      dataTrack={dataTrack}
                      mount={mount}
                      animStart
                    />
                  </Container>
                </HeroContentBottom>
              </HeroContent>
            </React.Fragment>
          ))}
        </HeroSection>
      )}
    </>
  );
};
LCVProductBanner.displayName = 'SectionMY24ProductHeroBanner';
export default LCVProductBanner;
